import { Box, Grid, makeStyles } from '@material-ui/core'
import React, { useCallback, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { CloseRounded } from '@material-ui/icons'
import clsx from 'clsx'
import Text from '../../../../../atoms/Text'
import { TEXT_VARIANTS } from '../../../../../../constants'
import SydLabel from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import Checkbox from '../../../../../molecules/Checkbox'
import SydSelect from '../../../../../commonDesign/SydSelect'
import SydTextArea from '../../../../../commonDesign/SydTextArea'
import { useListReportObjects } from '../../hooks/useListReportObjects'
import { usePreviewDisplayContext } from '../../components/PreviewDisplayContext'
import SydButton from '../../../../../commonDesign/Button'
import { getRandomId } from '../../../../../organisms/EntityGraph/NodeLinkComponents/utils'

const useStyles = makeStyles({
  selectedBlockContainer: {
    position: 'absolute',
    right: '634px',
    top: '40px',
    bottom: 50,
    zIndex: 10,
    width: '600px',
    transform: 'translateX(100%)',
    transition: '.25s ease-in-out',
    border: 'var(--border-neutral-normal)',
    borderRadius: 'var(--border-radius-md)',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    boxShadow: 'var(--shadow-md)',
    overflow: 'hidden',
    padding: 'var(--padding-md)',
    background: 'var(--card-bg-color)',
    '&.active': {
      transform: 'translateX(0)',
      transition: '.25s ease-in-out',
      bottom: 'auto'
    }
  },
  defaultValuesTitle: {
    fontSize: '8px',
    margin: '4px 0 0 0',
    color: 'var(--color-muted-neutral-500)'
  },
  defaultValues: {
    background: 'var(--color-neutral-100)',
    border: 'var(--border-neutral-normal)',
    borderRadius: 'var(--border-radius-sm)',
    padding: 'var(--padding-xs)',
    fontSize: 'var(--font-size-xs)',
    overflow: 'auto',
    width: '100%',
    maxHeight: '300px',
    margin: 0
  }
})

export const SelectedReportObjectEditCard = () => {
  const classes = useStyles()

  const { selectedReportObjectId, setSelectedReportObjectId } = usePreviewDisplayContext()
  const { control, watch, getValues, setValue } = useFormContext()
  const infographicBlockContent = watch('content')
  const infographicBlockMetadata = watch('metadata')

  const reportObjectIndex = useMemo(() => infographicBlockContent.findIndex(reportObject => reportObject.id === selectedReportObjectId), [infographicBlockContent, selectedReportObjectId])
  const formBasePath = useMemo(() => `content[${reportObjectIndex}]`, [reportObjectIndex])
  const reportBlockContent = useMemo(() => infographicBlockContent[reportObjectIndex], [infographicBlockContent, reportObjectIndex])

  const showFillHeightText = useMemo(() => infographicBlockContent?.length <= 1 && !reportBlockContent?.rows, [infographicBlockContent, reportBlockContent])
  const showFillWidthText = useMemo(() => infographicBlockContent?.length <= 1 && !reportBlockContent?.cols, [infographicBlockContent, reportBlockContent])

  const { data: reportObjects, isLoading: isLoadingReportObjects } = useListReportObjects()

  const reportObjectMaxCords = useMemo(() => {
    return {
      y: (infographicBlockMetadata?.size.rows ?? 12) - (reportBlockContent?.size?.rows ?? 0),
      x: (infographicBlockMetadata?.size.cols ?? 16) - (reportBlockContent?.size?.cols ?? 0)
    }
  }, [infographicBlockMetadata, reportBlockContent?.size])

  const reportObjectMaxSizes = useMemo(() => {
    return {
      cols: (infographicBlockMetadata?.size.cols ?? 12) - (reportBlockContent?.cords?.x ?? 0),
      rows: (infographicBlockMetadata?.size.rows ?? 16) - (reportBlockContent?.cords?.y ?? 0)
    }
  }, [infographicBlockMetadata, reportBlockContent?.cords])

  const reportObjectConfigs = useMemo(() => {
    const selectedComponent = reportBlockContent?.component
    if (!selectedComponent) return []

    const componentRequirementProps = reportObjects?.find(ro => ro.name === selectedComponent)?.requirementProps

    if (!Object.keys(componentRequirementProps ?? {})?.length) return []

    return Object.entries(componentRequirementProps).reduce((acc, [_key, value]) => {
      acc.push(
        value.configKeys?.map(configKey => ({
          configKey,
          defaultProps: value?.defaultProps[configKey]
        }))
      )
      return acc
    }, [])?.flat()
  }, [reportBlockContent?.component, reportObjects])

  const defaultProps = useMemo(() => {
    return reportObjects?.find(ro => ro.name === reportBlockContent?.component)?.component?.defaultProps ?? {}
  }, [reportBlockContent?.component, reportObjects])

  const onBlockChange = useCallback((key, value) => {
    const newValue = getValues('content').map(reportObject => {
      if (reportObject.id !== selectedReportObjectId) return reportObject
      if (key.includes('.')) {
        const [parentKey, childKey] = key.split('.')
        return {
          ...reportObject,
          [parentKey]: {
            ...reportObject[parentKey],
            [childKey]: value
          }
        }
      }
      return {
        ...reportObject,
        [key]: value
      }
    })
    setValue('content', newValue)
  }, [getValues, selectedReportObjectId, setValue])

  const onChangeComponent = useCallback((value, field) => {
    const newId = `${value}-${getRandomId()}`
    const newValue = getValues('content').map(reportObject => {
      if (reportObject.id !== selectedReportObjectId) return reportObject
      return {
        ...reportObject,
        id: newId,
        component: value,
        props: defaultProps
      }
    })
    setValue('content', newValue)
    setSelectedReportObjectId(newId)
    field.onChange(value)
  }, [defaultProps, getValues, selectedReportObjectId, setSelectedReportObjectId, setValue])

  const onChangeRuntimeConfigValues = useCallback((key, value) => {
    const newValue = {
      ...getValues('runtimeConfigValues'),
      [key]: value
    }
    setValue('runtimeConfigValues', newValue)
  }, [getValues, setValue])

  const handleSizeChange = useCallback(async (key, value) => {
    const normalizedValue = parseInt(value, 10)
    if (isNaN(normalizedValue)) return

    onBlockChange(key, normalizedValue)
  }, [onBlockChange])

  return (
    <div
      className={clsx(classes.selectedBlockContainer, { active: !!selectedReportObjectId })}
    >
      <div className='mb-2 flex gap-2 justify-between items-center'>
        <div>
          <Text variant={TEXT_VARIANTS.h3} text='Selected Block Properties' />
          <Text
            variant={TEXT_VARIANTS.caption}
            text='Change display settings'
          />
        </div>

        <SydButton
          type='button'
          size='xs'
          onClick={() => setSelectedReportObjectId(null)}
          style={{ minWidth: 0, padding: 'var(--padding-xs)' }}
        >
          <CloseRounded fontSize='small' />
        </SydButton>
      </div>

      <Controller
        control={control}
        name={`${formBasePath}.component`}
        render={({ field }) => (
          <SydLabel label='Report Object'>
            <SydSelect
              size='sm'
              options={reportObjects?.map((reportObject) => ({
                value: reportObject.name,
                label: reportObject.name,
                description: reportObject.description
              })) ?? []}
              {...field}
              value={watch(`${formBasePath}.component`) ?? ''}
              onChange={(e) => onChangeComponent(e.target.value, field)}
              placeholder={isLoadingReportObjects ? 'Loading...' : 'Select a report object'}
            />
          </SydLabel>
        )}
      />

      <hr style={{ margin: '1rem 0' }} />
      <SydLabel label='Starting Location' style={{ fontSize: '14px' }} />
      <Box display='flex' gridGap='12px'>
        <Box flex={1}>
          <Controller
            control={control}
            name={`${formBasePath}.cords.y`}
            render={({ field }) => (
              <SydLabel
                label='Row'
                description={(
                  <>
                    Determines what row this item starts on.<br />
                    (Min: 0 / Max: {reportObjectMaxCords.y}
                  </>
                )}
              >
                <SydInput
                  size='sm'
                  type='number'
                  minWidth='100%'
                  inputProps={{
                    increment: 1,
                    min: 0,
                    max: reportObjectMaxCords.y
                  }}
                  {...field}
                  value={watch(`${formBasePath}.cords.y`) ?? ''}
                  onChange={(e) => handleSizeChange('cords.y', e.target.value)}
                />
              </SydLabel>
            )}
          />
        </Box>

        <Box flex={1}>
          <Controller
            control={control}
            name={`${formBasePath}.cords.x`}
            render={({ field }) => (
              <SydLabel
                label='Column'
                description={(
                  <>
                    Determines what column this item starts on.<br />
                    (Min: 0 / Max: {reportObjectMaxCords.x}
                  </>
                )}
              >
                <SydInput
                  size='sm'
                  type='number'
                  minWidth='100%'
                  inputProps={{
                    increment: 1,
                    min: 0,
                    max: reportObjectMaxCords.x
                  }}
                  {...field}
                  value={watch(`${formBasePath}.cords.x`) ?? ''}
                  onChange={(e) => handleSizeChange('cords.x', e.target.value)}
                />
              </SydLabel>
            )}
          />
        </Box>
      </Box>

      <hr style={{ margin: '1rem 0' }} />
      <SydLabel label='Size' style={{ fontSize: '14px' }} />
      <Box display='flex' gridGap='12px'>
        <Box flex={1}>
          <Controller
            control={control}
            name={`${formBasePath}.size.rows`}
            render={({ field }) => (
              <SydLabel
                label='Height (Rows)'
                description={(
                  <>
                    Determines how many rows this selectedBlock takes up.<br />
                    (Min: 1 / Max: {reportObjectMaxSizes.rows})
                    {showFillHeightText && (
                      <>
                        <hr />
                        Since this is the only object in this block and it starts at the top, it will fill the full
                        space of the block
                      </>
                    )}
                  </>
                )}
              >
                <SydInput
                  size='sm'
                  type='number'
                  minWidth='100%'
                  inputProps={{
                    increment: 1,
                    min: 1,
                    max: reportObjectMaxSizes.rows
                  }}
                  {...field}
                  value={watch(`${formBasePath}.size.rows`) ?? ''}
                  onChange={(e) => handleSizeChange('size.rows', e.target.value)}
                />
              </SydLabel>
            )}
          />
        </Box>
        <Box flex={1}>
          <Controller
            control={control}
            name={`${formBasePath}.size.cols`}
            render={({ field }) => (
              <SydLabel
                label='Width (Columns)'
                description={(
                  <>
                    Determines how many columns this selectedBlock takes up.<br />
                    (Min: 1 / Max: {reportObjectMaxSizes.cols})
                    {showFillWidthText && (
                      <>
                        <hr />
                        Since this is the only object in this block and it starts at the left, it will fill the full space of the block
                      </>
                    )}
                  </>
                )}
              >
                <SydInput
                  size='sm'
                  type='number'
                  minWidth='100%'
                  inputProps={{
                    increment: 1,
                    min: 1,
                    max: reportObjectMaxSizes.cols
                  }}
                  {...field}
                  value={watch(`${formBasePath}.size.cols`) ?? ''}
                  onChange={(e) => handleSizeChange('size.cols', e.target.value)}
                />
              </SydLabel>
            )}
          />
        </Box>
      </Box>
      <hr style={{ margin: '1rem 0' }} />

      <Controller
        control={control}
        name={`${formBasePath}.hideInHtml`}
        render={({ field }) => (
          <Box display='flex' gridGap={4}>
            <Checkbox
              checked={!!watch(`${formBasePath}.hideInHtml`)}
              onChange={(e) => onBlockChange('hideInHtml', !!e.target.checked)}
            />
            <Box onClick={(e) => field.onChange({ ...e, target: { value: !field.value } })}>
              <SydLabel
                label='Hide in HTML Delivery'
                description='Will only show in PDF Delivery / export'
              />
            </Box>
          </Box>
        )}
      />

      <hr style={{ margin: '1rem 0' }} />

      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <SydLabel label='Data Configuration' style={{ fontSize: '14px' }} />
          {reportObjectConfigs?.map(({ configKey, defaultProps }) => (
            <Box key={configKey} mb={2}>
              <Controller
                key={`runtimeConfigValues.${configKey}`}
                control={control}
                name={`runtimeConfigValues.${configKey}`}
                render={({ field }) => (
                  <SydLabel label={configKey}>
                    <SydTextArea
                      size='sm'
                      minWidth='100%'
                      inputProps={{
                        rows: 8
                      }}
                      defaultValue={JSON.stringify(watch(`runtimeConfigValues[${configKey}]`), null, 2)}
                      onChange={(e) => onChangeRuntimeConfigValues(configKey, JSON.parse(e.target.value))}
                    />
                    <Box mt={2}>
                      <p className={classes.defaultValuesTitle}>Default Properties</p>
                      <pre className={classes.defaultValues}>
                        {JSON.stringify(reportObjectConfigs, null, 2)}
                      </pre>
                    </Box>
                  </SydLabel>
                )}
              />
            </Box>
          ))}
        </Grid>
        <Grid item xs={12} sm={6}>
          <SydLabel label='Component Display Properties' style={{ fontSize: '14px' }}>
            <Box mb={2}>
              <Controller
                key={`${formBasePath}.props`}
                control={control}
                name={`${formBasePath}.props`}
                render={({ field }) => (
                  <>
                    <SydTextArea
                      size='sm'
                      minWidth='100%'
                      inputProps={{
                        rows: 4
                      }}
                      defaultValue={JSON.stringify(watch(`${formBasePath}.props`), null, 2)}
                      onChange={(e) => onBlockChange('props', JSON.parse(e.target.value))}
                    />
                    <div className='mt-2'>
                      <p className={classes.defaultValuesTitle}>Default Properties</p>
                      <pre className={classes.defaultValues}>
                        {JSON.stringify(defaultProps, null, 2)}
                      </pre>
                    </div>
                  </>
                )}
              />
            </Box>
          </SydLabel>
        </Grid>
      </Grid>
    </div>
  )
}
